/* eslint max-lines: 0 */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components/macro';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'models';
import actions from 'state/actions';
import selectors from 'state/selectors';
import { modalActionType, actionType, docHeaderExcluded } from 'utils/constants';
import intlHelper from 'utils/helpers/intl.helper';
import { useFormikContext } from 'formik';
import Toolbar from 'components/Toolbar';
import utils from 'utils';
import { MovTypeValue, Q1Value, FormValues } from 'models/documents';
import { DataGridRow } from 'components/DataGrid';
import ToolbarButton from './buttons';
import CommonButton from 'components/Buttons/CommonButton';
import { useLocation } from 'react-router';
import { useTheme } from 'providers/ThemeProvider';

const ToolbarWrapper = styled.div`
  position: relative;
`;

const ToolbarOverlay = styled.div<{ isDisabled: boolean }>`
  background: ${({ theme }) => theme.colorPalette.white}99;
  display: ${({ isDisabled }) => (isDisabled ? 'block' : 'none')};
  z-index: ${({ theme }) => theme.zIndexPalette.highest};
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
`;

const ToolBarDetails = () => {
  const [more, setMore] = useState(false);
  const t = intlHelper.useTranslator();
  const history = useHistory();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const programName = useSelector(selectors.app.getProgramName);
  const documentFlatFields = useSelector(selectors.documents.selectDocumentFlatFields);
  const templateActionsOpen = useSelector(selectors.documents.selectTemplateActionsOpen);
  const { dirty, values } = useFormikContext<FormValues | { [key: string]: Q1Value | MovTypeValue }>();
  const isDocReadOnly = useSelector(selectors.documents.selectIsDocReadOnly);
  const location = useLocation();
  const saveAction = utils.hooks.useSaveAction();
  const resetManualDirty = utils.hooks.useResetManualDirty();
  const { isDarkMode } = useTheme();

  const autosave =
    useSelector((store: RootState) => selectors.app.getConfig(store, actionType.AUTOSAVE_DOCUMENT))?.val === 1;

  const exportCSV = () => {
    try {
      let csv = '';

      // Q1
      const Q1Header = documentFlatFields.map(({ translate, fieldName }) => ({
        accessor: fieldName,
        label: translate,
      }));
      const Q1Body: { [key: string]: string } = {};
      for (const fieldName in values) {
        if (Object.prototype.hasOwnProperty.call(values, fieldName) && !docHeaderExcluded.includes(fieldName)) {
          Q1Body[fieldName] = (values[fieldName] as Q1Value).content[0].content;
        }
      }
      csv = utils.file.convertToCSV(Q1Header, [Q1Body]);
      // Q1

      // Q3/body
      const inputWithQ3: { [key: string]: DataGridRow[] } = Object.entries(values).reduce((acc, [inputName, value]) => {
        const body = (value as Q1Value)?.body;
        if (!docHeaderExcluded.includes(inputName) && body != null && body.length > 0) {
          return {
            ...acc,
            [inputName]: body,
          };
        }
        return { ...acc };
      }, {});

      Object.entries(inputWithQ3).forEach(([inputName, dataRows]) => {
        // find the columns
        const columns = documentFlatFields.find((field) => field.fieldName === inputName)?.fieldGroup?.fieldColumn;
        if (columns) {
          const header = columns?.map(({ title, code }) => ({ accessor: `${code}`, label: title })) || [];
          const body =
            dataRows?.map((row) => {
              const enhancedRow: { [key: string]: string } = {};
              const rowIDs = Object.keys(row);
              rowIDs.forEach((rowID) => {
                enhancedRow[rowID] = row[rowID].content || '';
              });
              return enhancedRow;
            }) || [];
          csv += `\r\n\r\n${inputName}\r\n${utils.file.convertToCSV(header, body)}`;
        }
      });
      // Q3/body

      // download
      utils.file.downloadCsv(`${protocol}`, csv);
    } catch (e) {
      utils.app.notify('fail', e as Error);
      console.error(e);
    }
  };

  const manualDirty = (values?.movTypeHeader as MovTypeValue)?.changed === true;

  const goBackToTheDocumentList = useCallback(
    async (e: React.MouseEvent | KeyboardEvent, force?: boolean, manualDirty?: boolean) => {
      try {
        // These variables are used in the Contracts Archive module
        // When navigating to a document detail from the
        // "View Previous Documents" modal, we need to know where we came from.
        const state = location.state as {
          fromContractViewPreviousDocModal?: boolean;
          sourceDocumentId?: number;
        };

        const fromModal = state?.fromContractViewPreviousDocModal;
        const sourceDocId = state?.sourceDocumentId;

        if (autosave && (dirty || manualDirty)) {
          await saveAction(values as FormValues);
          resetManualDirty(values as FormValues);
        }
        if (!autosave && (dirty || manualDirty) && !force) {
          return dispatch(
            actions.modal.openModal(modalActionType.documents.CONTINUE_WITHOUT_SAVING, {
              forceValidation: () => goBackToTheDocumentList(e, true),
            }),
          );
        }

        if (fromModal && sourceDocId) {
          dispatch(actions.documents.setActiveDocumentId([sourceDocId]));
        }
        history.goBack();
      } catch (e) {
        console.error(e);
      }
    },
    [autosave, dirty, saveAction, resetManualDirty, values, dispatch, history, location.state],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F3') {
        event.preventDefault();
        goBackToTheDocumentList(event, false, manualDirty);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dirty, manualDirty, values, saveAction, resetManualDirty, goBackToTheDocumentList]);
  return (
    <ToolbarWrapper>
      <ToolbarOverlay isDisabled={isDocReadOnly} />
      <Toolbar.BlueBar>
        <Toolbar.Left>
          <CommonButton
            id="back-to-list-btn"
            scope={isDarkMode ? 'primary' : 'secondary'}
            value={t('back-to-list')}
            action={(e) => goBackToTheDocumentList(e, false, manualDirty)}
          />
          <CommonButton
            id="actions-btn"
            selected={true}
            disabled={!more}
            scope={isDarkMode ? 'primary' : 'secondary'}
            value={t('Actions')}
            action={() => setMore(false)}
          />
          <CommonButton
            id="more-btn"
            disabled={more}
            scope={isDarkMode ? 'primary' : 'secondary'}
            value={t('More')}
            action={() => setMore(true)}
          />
        </Toolbar.Left>
        <Toolbar.Right>
          <Toolbar.ModuleName>{programName}</Toolbar.ModuleName>
        </Toolbar.Right>
      </Toolbar.BlueBar>
      <Toolbar.ActionBar>
        <Toolbar.ButtonsWrapper>
          <Toolbar.ButtonsContainer hidden={!more}>
            <CommonButton
              id="file-csv-btn"
              action={exportCSV}
              scope="tertiary"
              value={t('csv')}
              customIcon="file-csv"
            />
            <CommonButton
              id="hot-keys-btn"
              value={t('hot-keys')}
              scope="tertiary"
              customIcon="keyboard"
              action={() => dispatch(actions.modal.openModal(modalActionType.documents.HOT_KEYS))}
            />
          </Toolbar.ButtonsContainer>
          <Toolbar.ButtonsContainer hidden={more}>
            {templateActionsOpen?.map((e) => (
              <ToolbarButton key={e} action={e} />
            ))}
          </Toolbar.ButtonsContainer>
        </Toolbar.ButtonsWrapper>
      </Toolbar.ActionBar>
    </ToolbarWrapper>
  );
};

export default ToolBarDetails;
